import { useNavigate } from "@solidjs/router";
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { type BookingView, type TripView, VEHICLE_ID_RE } from "../../shared/api";
import { vehicleIdFromQr } from "../../shared/qr";
import { ApiFailure, api } from "../api";
import { AppBar } from "../components/AppBar";
import { Button } from "../components/Button";
import { LoadingPill } from "../components/Spinner";
import { TextField } from "../components/TextField";
import { cancelBooking } from "../state/booking";
import { freshPosition, geo, watchPosition } from "../state/geo";
import { setTorch, torchSupported } from "../torch";

declare global {
  interface Window {
    BarcodeDetector?: new (opts?: { formats: string[] }) => {
      detect(src: ImageBitmapSource): Promise<{ rawValue: string }[]>;
    };
  }
}

const ERRORS: Record<string, string> = {
  VEHICLE_NOT_AVAILABLE: "Ce vélo n'est pas disponible.",
  VEHICLE_NO_LONGER_AVAILABLE: "Ce vélo vient d'être pris. Choisissez-en un autre.",
  UNKNOWN_VEHICLE: "Numéro de vélo inconnu.",
  ACCOUNT_LINK_FAILED: "Votre compte n'a pas pu être relié au service vélo. Contactez le support.",
  ONGOING_BOOKING: "Vous avez déjà un trajet en cours.",
  PAYMENT_FAILED: "Le paiement a été refusé. Vérifiez votre moyen de paiement.",
  PAYMENT_METHOD_REQUIRED: "Ajoutez un moyen de paiement pour rouler.",
  CUSTOMER_BLOCKED: "Votre compte ne permet pas de rouler. Contactez le support.",
};

/** Figma 05 Scan QR (BarcodeDetector when available) and 05c manual entry. */
export function Scan() {
  const navigate = useNavigate();
  // Camera needs a secure context (HTTPS, or localhost): see `vp run dev:https` for phones on the LAN.
  const cameraAvailable = window.isSecureContext && !!navigator.mediaDevices?.getUserMedia;
  // Always the scan first, even for a booked bike (Figma 14:8184); the manual entry starts empty.
  const [manual, setManual] = createSignal(!cameraAvailable);
  const [code, setCode] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string>();
  /** A booking on another vehicle blocks this start: offer to cancel it (Pony allows one at a time). */
  const [conflict, setConflict] = createSignal<{ booking: BookingView; wanted: string }>();
  watchPosition(); // Pony gets the rider's position with the unlock
  const [cameraReady, setCameraReady] = createSignal(false);
  const [cancelling, setCancelling] = createSignal(false);
  const [torchTrack, setTorchTrack] = createSignal<MediaStreamTrack>();
  const [torchOn, setTorchOn] = createSignal(false);
  let video!: HTMLVideoElement;
  let stream: MediaStream | undefined;
  let timer: number | undefined;

  const start = async (vehicleId: string) => {
    const id = vehicleId.trim().toUpperCase();
    if (!VEHICLE_ID_RE.test(id)) return setError("Numéro de vélo invalide.");
    if (busy()) return;
    setBusy(true);
    setError(undefined);
    try {
      const trip = await api.post<TripView>(
        "/trips",
        { vehicleId: id, position: geo.located() ? geo.position() : await freshPosition(3_000) },
        60_000,
      );
      navigate("/ride", { replace: true, state: { unlocked: trip.id } });
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "INTERNAL";
      if (code === "ONGOING_BOOKING") {
        const ride = await api.get<TripView>("/trips/current").catch(() => undefined);
        if (ride) return navigate("/ride", { replace: true });
        const booked = await api.get<BookingView>("/bookings/current").catch(() => undefined);
        if (booked && booked.vehicleId !== id) {
          setConflict({ booking: booked, wanted: id });
          setBusy(false);
          return;
        }
      }
      if (code === "PHONE_VERIFICATION_REQUIRED")
        return navigate(`/phone?vehicle=${encodeURIComponent(id)}`);
      if (code === "PAYMENT_METHOD_REQUIRED")
        return navigate(`/payment?vehicle=${encodeURIComponent(id)}`);
      setError(ERRORS[code] ?? "Impossible de déverrouiller le vélo. Réessayez.");
      setBusy(false);
    }
  };

  /** QR decoder: the native BarcodeDetector (Android Chrome), else jsQR (iPhone Safari has none). */
  async function qrDecoder(): Promise<(v: HTMLVideoElement) => Promise<string | undefined>> {
    if (window.BarcodeDetector) {
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      return async (v) => (await detector.detect(v).catch(() => []))[0]?.rawValue;
    }
    const { default: jsQR } = await import("jsqr");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    return async (v) => {
      const k = Math.min(1, 640 / (v.videoWidth || 640)); // downscale: fast enough every 300 ms
      canvas.width = Math.round(v.videoWidth * k);
      canvas.height = Math.round(v.videoHeight * k);
      if (!ctx || !canvas.width) return undefined;
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      return jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" })?.data;
    };
  }

  const stopCamera = () => {
    if (timer) clearInterval(timer);
    timer = undefined;
    for (const t of stream?.getTracks() ?? []) t.stop();
    stream = undefined;
    setCameraReady(false);
    setTorchTrack(undefined);
    setTorchOn(false);
  };

  async function startCamera() {
    setManual(false);
    setError(undefined);
    await new Promise(requestAnimationFrame); // the <video> exists only once camera mode rendered
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      video.srcObject = stream;
      await video.play();
      const track = stream.getVideoTracks()[0];
      if (torchSupported(track)) setTorchTrack(track); // flashlight button only when supported
      const decode = await qrDecoder();
      setCameraReady(true);
      timer = window.setInterval(async () => {
        if (busy() || conflict() || video.readyState < 2) return;
        const v = await decode(video);
        if (!v) return;
        const id = vehicleIdFromQr(v);
        if (id) void start(id);
        else setError("Ce QR code n'est pas celui d'un vélo.");
      }, 300);
    } catch {
      stopCamera();
      setManual(true);
      setError("Caméra indisponible : saisissez le numéro du vélo.");
    }
  }

  /** "Saisir le numéro" (05 → 05c): the camera stops so it can't unlock a bike behind the form. */
  const toManual = () => {
    stopCamera();
    setError(undefined);
    setManual(true);
  };

  const toggleTorch = async () => {
    const t = torchTrack();
    if (t && (await setTorch(t, !torchOn()))) setTorchOn(!torchOn());
  };

  onMount(() => {
    if (!manual()) void startCamera();
  });
  onCleanup(stopCamera);

  /** Another bike is booked: cancel that booking, then start the wanted one. */
  const conflictPanel = (c: { booking: BookingView; wanted: string }) => (
    <div class="rounded-xl bg-surface2 px-4 py-3 text-sm text-strong" role="alert">
      <p>
        Vous avez réservé {c.booking.vehicleId}. Annulez cette réservation pour démarrer {c.wanted}.
      </p>
      <Button
        variant="outline"
        class="mt-3"
        loading={cancelling()}
        onClick={async () => {
          const { booking, wanted } = c;
          setCancelling(true);
          const r = await cancelBooking(booking.id);
          setCancelling(false);
          if (!r.cancelled) return setError(r.message); // still booked: don't start
          setConflict(undefined);
          void start(wanted);
        }}
      >
        Annuler et démarrer
      </Button>
      <button
        type="button"
        class="mt-2 w-full py-2 text-sm text-violet underline"
        onClick={() => setConflict(undefined)}
      >
        Garder ma réservation
      </button>
    </div>
  );

  return (
    <main class="screen bg-surface">
      <AppBar
        title={manual() ? "Saisir le numéro" : "Scanner le vélo"}
        back={manual() && cameraAvailable ? () => void startCamera() : "/map"}
        close
      />
      <Show
        when={manual()}
        fallback={
          <div class="relative flex-1 overflow-hidden bg-[#1a1a1a] text-white flex flex-col">
            <video
              ref={video}
              class="absolute inset-0 h-full w-full object-cover"
              muted
              playsinline
            />
            <div class="relative z-10 px-5 pt-[42px] text-center short:pt-5">
              <h1 class="text-[21px] font-medium leading-[1.25] tracking-[0.21px]">
                Visez le QR code situé sur le guidon
              </h1>
              <p class="mx-auto mt-[14px] max-w-[342px] text-sm leading-[1.25] tracking-[0.14px]">
                Une empreinte bancaire de 5 € est réalisée, puis ajustée au prix réel à la fin du
                trajet.
              </p>
            </div>
            {/* 240 px clear window: its huge shadow is the 55 % mask, clipped by overflow-hidden. */}
            <div class="relative mx-auto mt-[83px] h-[240px] w-[240px] shrink-0 shadow-[0_0_0_200vmax_rgba(0,0,0,0.55)] flex items-center justify-center short:mt-6">
              <ScanCorners />
              <Show when={busy() || !cameraReady()}>
                <LoadingPill
                  label={busy() ? "Déverrouillage du vélo…" : "Ouverture de la caméra…"}
                />
              </Show>
            </div>
            <div class="relative z-10 mt-[42px] flex flex-col items-center gap-3 px-[15px] short:mt-5">
              <Show when={torchTrack()}>
                <button
                  type="button"
                  class={`h-14 w-14 rounded-full flex items-center justify-center ${torchOn() ? "bg-green" : "bg-white"}`}
                  aria-label={torchOn() ? "Éteindre la lampe" : "Allumer la lampe"}
                  aria-pressed={torchOn()}
                  onClick={() => void toggleTorch()}
                >
                  <img src="/icons/flashlight.svg" width="28" height="28" alt="" />
                </button>
              </Show>
              <Show when={error()}>
                {(m) => (
                  <p
                    class="w-full rounded-[4px] bg-white/95 px-3 py-2 text-center text-sm text-[#e53935]"
                    role="alert"
                  >
                    {m()}
                  </p>
                )}
              </Show>
              <Show when={conflict()}>{(c) => <div class="w-full">{conflictPanel(c())}</div>}</Show>
            </div>
            <div
              class="relative z-10 mt-auto grid grid-cols-2 gap-3 px-[15px] pt-4"
              style={{ "padding-bottom": "max(1rem, env(safe-area-inset-bottom))" }}
            >
              <button type="button" class={DARK_OUTLINE} onClick={toManual}>
                Saisir le numéro
              </button>
              {/* Group ride (second bike) is out of the PoC scope. */}
              <button type="button" class={`${DARK_OUTLINE} opacity-50`} disabled>
                Trajet de groupe
              </button>
            </div>
          </div>
        }
      >
        <form
          class="screen-body px-[15px] pt-[22px] short:pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            // Close the keyboard before the ride screen replaces this form (see viewport.ts).
            (document.activeElement as HTMLElement | null)?.blur();
            void start(code());
          }}
        >
          <h1 class="px-[5px] text-[21px] font-medium leading-[1.25] tracking-[0.21px] text-violet">
            Numéro du vélo
          </h1>
          <p class="mt-2 px-[5px] text-[15px] leading-[1.25] tracking-[0.15px] text-text">
            Le numéro figure sous le QR code, sur le guidon du vélo.
          </p>
          <TextField
            id="code"
            label="Numéro du vélo :"
            class="mt-[10px]"
            inputClass="uppercase"
            autocapitalize="characters"
            autocomplete="off"
            placeholder="Ex. : E001111"
            value={code()}
            onInput={(e) => setCode(e.currentTarget.value)}
          />
          <img
            src="/img/ebike.webp"
            alt=""
            width="372"
            height="160"
            class="mt-[22px] h-[160px] w-full object-contain short:h-[110px]"
          />
          <Show when={cameraAvailable}>
            <button
              type="button"
              class="mx-auto mt-2 py-2 text-sm text-link underline"
              onClick={() => void startCamera()}
            >
              Scanner le QR code
            </button>
          </Show>
          <Show when={error()}>
            {(m) => (
              <p class="mt-3 text-center text-sm text-[#e53935]" role="alert">
                {m()}
              </p>
            )}
          </Show>
          <Show when={conflict()}>{(c) => <div class="mt-4">{conflictPanel(c())}</div>}</Show>
          <div class="screen-footer">
            <Button type="submit" loading={busy()} disabled={!code().trim()}>
              Valider
            </Button>
          </div>
        </form>
      </Show>
    </main>
  );
}

/** Figma 05 outline-on-dark button: 50 px, 1.5 px white border, white Medium 16 uppercase. */
const DARK_OUTLINE =
  "h-[50px] rounded-[4px] border-[1.5px] border-white bg-transparent text-base font-medium leading-[1.25] uppercase tracking-[0.32px] text-white active:bg-white/10";

/** Green corners 44 x 5 + 5 x 44, outer edges 5 px outside the 240 px window (Figma 05). */
function ScanCorners() {
  const bar = "absolute rounded-[2px] bg-green";
  return (
    <div class="pointer-events-none absolute -inset-[5px]" aria-hidden="true">
      <span class={`${bar} left-0 top-0 h-[5px] w-[44px]`} />
      <span class={`${bar} left-0 top-0 h-[44px] w-[5px]`} />
      <span class={`${bar} right-0 top-0 h-[5px] w-[44px]`} />
      <span class={`${bar} right-0 top-0 h-[44px] w-[5px]`} />
      <span class={`${bar} bottom-0 left-0 h-[5px] w-[44px]`} />
      <span class={`${bar} bottom-0 left-0 h-[44px] w-[5px]`} />
      <span class={`${bar} bottom-0 right-0 h-[5px] w-[44px]`} />
      <span class={`${bar} bottom-0 right-0 h-[44px] w-[5px]`} />
    </div>
  );
}
